import { useState, useEffect } from 'react';
import { personService } from '@shared/services/personService';
import { useForm } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import * as yup from 'yup';
import { useTranslation } from 'react-i18next';

export const useBanks = () => {
    const { t } = useTranslation();
    const [banks, setBanks] = useState([]);
    const [selectedBankId, setSelectedBankId] = useState(null);
    const [selectedBank, setSelectedBank] = useState(null);
    const [modalState, setModalState] = useState({ visible: false, type: null });
    const [showDeleteModal, setShowDeleteModal] = useState(false);
    const [pageNumber, setPageNumber] = useState(0);
    const [pageSize, setPageSize] = useState(10);
    const [totalElements, setTotalElements] = useState(0);
    const [totalPages, setTotalPages] = useState(0);
    const [sortBy, setSortBy] = useState('name');
    const [sortDir, setSortDir] = useState('asc');
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState(null);

    const validationSchema = yup.object({
        bankCode: yup.string().required(t('validation.person.banks.bankCodeRequired')),
        name: yup.string().required(t('validation.person.banks.nameRequired')),
        address: yup.string(),
        active: yup.boolean()
    });

    const {
        register,
        handleSubmit,
        formState: { errors },
        setValue,
        reset
    } = useForm({
        resolver: yupResolver(validationSchema),
        defaultValues: {
            bankCode: '',
            name: '',
            address: '',
            active: true
        }
    });

    const fetchBanks = async () => {
        setLoading(true);
        setError(null);
        try {
            const response = await personService.getBanks({
                page: pageNumber,
                size: pageSize,
                sortBy,
                sortDir
            });
            setBanks(response._embedded?.banksDTOList || []);
            setTotalElements(response.page?.totalElements || 0);
            setTotalPages(Math.ceil(response.page?.totalElements / pageSize) || 0);
       
        } catch (error) {
            console.error('Error fetching banks:', error);
            setError(t('common.error.errorFetchingData'));
            setBanks([]);
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        fetchBanks();
    }, [pageNumber, pageSize, sortBy, sortDir]);

    useEffect(() => {
        if (modalState.type === 'edit' && selectedBank) {
            setValue('bankCode', selectedBank.bankCode || '');
            setValue('name', selectedBank.name || '');
            setValue('address', selectedBank.address || '');
            setValue('active', selectedBank.active ?? true);
        } else {
            reset();
        }
    }, [modalState.type, selectedBank, setValue, reset]);

    const handleSelectBank = (bank) => {
        const bankId = bank.branchId;
        if (selectedBankId === bankId) {
            setSelectedBankId(null);
            setSelectedBank(null);
        } else {
            setSelectedBankId(bankId);
            setSelectedBank(bank);
        }
    };

    const handleOpenModal = (type, bank = null) => {
        setModalState({ visible: true, type });
        setSelectedBank(bank);
    };

    const handleCloseModal = () => {
        setModalState({ visible: false, type: null });
        setSelectedBank(null);
        reset();
    };

    const handleDeleteConfirm = async () => {
        try {
            await personService.deleteBank(selectedBankId);
            await fetchBanks();
            setShowDeleteModal(false);
            setSelectedBankId(null);
            setSelectedBank(null);
        } catch (error) {
            console.error('Error deleting bank:', error);
        }
    };

    const handleSaveBank = async (formData) => {
        try {
            if (modalState.type === 'add') {
                await personService.addBank(formData);
            } else if (modalState.type === 'edit' && selectedBank) {
                await personService.updateBank(selectedBank.branchId, formData);
            }
            await fetchBanks();
            handleCloseModal();
        } catch (error) {
            console.error('Error saving bank:', error);
        }
    };

    const handleShowDeleteModal = () => {
        if (selectedBank) {
            setShowDeleteModal(true);
        }
    };

    const handleSort = (field) => {
        if (sortBy === field) {
            setSortDir(sortDir === 'asc' ? 'desc' : 'asc');
        } else {
            setSortBy(field);
            setSortDir('asc');
        }
        setPageNumber(0);
    };

    return {
        banks,
        selectedBank,
        selectedBankId,
        modalState,
        showDeleteModal,
        pageNumber,
        pageSize,
        totalElements,
        totalPages,
        sortBy,
        sortDir,
        loading,
        error,
        handleOpenModal,
        handleCloseModal,
        handleDeleteConfirm,
        handleSaveBank,
        handleSelectBank,
        handleShowDeleteModal,
        setShowDeleteModal,
        handleSort,
        setPageNumber,
        setPageSize,
        // Form related
        register,
        handleSubmit,
        errors,
        setValue
    };
};
